import i18n from '@i18n';
import { LocalStorage } from '@services/storage';
import Axios from 'axios';

import {
  API_URL,
  LOCAL_ACCESS_TOKEN,
  LOCAL_REFRESH_TOKEN,
  LOCAL_REFRESH_TOKEN_DATA,
} from './constants';
import { LINK_LOGIN } from './link';
import { HttpStatus } from './utility-property';

const defaultConfig = {
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
};

const refreshTokenUrl = '/api/token/refresh';
const http = Axios.create(defaultConfig);
const refreshTokenService = Axios.create(defaultConfig);

let isRefreshing = false;
let refreshPromise = null;

// 🧩 Запрос: добавляем access_token если есть
http.interceptors.request.use(({ auth = true, ...config }) => {
  const accessToken = LocalStorage.getAccessToken();

  return {
    ...config,
    headers: {
      ...config.headers,
      ...(accessToken && auth && { Authorization: `Bearer ${accessToken}` }),
    },
  };
});

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config;

    if (originalRequest?.skipInterceptor) {
      return Promise.reject(error.response || error);
    }

    if (originalRequest?.url?.includes(refreshTokenUrl)) {
      LocalStorage.clearToken();
      window.location.href = LINK_LOGIN;

      return Promise.reject(error);
    }

    // 🔐 Обработка 401
    if (status === HttpStatus.Unauthorized) {
      const refreshToken = LocalStorage.getRefreshToken();

      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;

          refreshPromise = refreshTokenService
            .post(
              refreshTokenUrl,
              {},
              {
                headers: {
                  Authorization: `Bearer ${LocalStorage.getAccessToken()}`,
                  'X-Auth-Token': LocalStorage.getRefreshToken(),
                },
              },
            )
            .then((res) => {
              isRefreshing = false;

              const refreshTokenData =
                new Date().getTime() + res.data.expires_in * 1000;

              LocalStorage.set(LOCAL_ACCESS_TOKEN, res.data.access_token);
              LocalStorage.set(LOCAL_REFRESH_TOKEN, res.data.refresh_token);
              LocalStorage.set(LOCAL_REFRESH_TOKEN_DATA, refreshTokenData);

              return res.access_token;
            })
            .catch((err) => {
              isRefreshing = false;
              LocalStorage.clearToken();
              window.location.href = LINK_LOGIN;
              throw err;
            });
        }

        const newAccessToken = await refreshPromise;

        // 🔁 Повторяем запрос, добавляя новый токен и X-Auth-Token
        const newHeaders = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`,
          ...(refreshToken && { 'X-Auth-Token': refreshToken }),
        };

        return http({
          ...originalRequest,
          headers: newHeaders,
        });
      }

      LocalStorage.clearToken();
      window.location.href = LINK_LOGIN;

      return Promise.reject(error);
    }

    if (
      status === HttpStatus.UnprocessableEntity &&
      error.response?.data?.errors
    ) {
      const { errors } = error.response.data;
      let message = '';

      for (const prop in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, prop)) {
          message += Array.isArray(errors[prop])
            ? `${errors[prop].join(', ')} `
            : `${errors[prop]} `;
        }
      }

      return Promise.reject(message.trim());
    }

    if (status === HttpStatus.Forbidden) {
      localStorage.clear();
      window.location.reload();

      return Promise.reject(error);
    }

    if (status && status > HttpStatus.ServerError) {
      return Promise.reject(new Error(i18n.t('ALERT.SERVER_ERROR')));
    }

    return Promise.reject(error.response?.data?.message || error.toString());
  },
);

export default http;
